import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ActionItem,
    ActionItemStyle,
    ApiFragment,
    ComplexMapFieldValue,
    DialogConfig,
    DialogRef,
    DialogStyle,
    FormValue,
    FormValueState,
    IconSize,
    MakeProvider,
    MultiFormSelectOptions,
    MultiValueFormInput,
    ShDynamicDialogService
} from '@stylehaven/components';
import { SelectionDialogOutput, SelectionDialogOutputSuccess } from '../../../../dialog/dialog.module';
import { WebserviceSearchDialogComponent } from '../../../components/webservice-search-dialog/webservice-search-dialog.component';
import { WebserviceConfig } from '../../../entities/webservice-config.entity';

@Component({
    standalone: false,
    selector: 'med-form-webservice-base-complex',
    templateUrl: './form-webservice-base-complex.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(FormWebserviceComplexBaseComponent)]
})
export class FormWebserviceComplexBaseComponent
    extends MultiValueFormInput<ComplexMapFieldValue>
    implements OnInit, OnDestroy
{
    private state$: BehaviorSubject<FormValue<ComplexMapFieldValue>[]> = new BehaviorSubject<
        FormValue<ComplexMapFieldValue>[]
    >([]);

    public isIndeterminate$!: Observable<boolean>;

    public tags$!: Observable<MultiFormSelectOptions<ComplexMapFieldValue>>;

    public suggestedConcepts$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public suggestionErrorMsg$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    public searchActionItem$: Observable<ActionItem | null>;

    public webserviceSearchControl: UntypedFormControl = new UntypedFormControl();
    public sizes: typeof IconSize = IconSize;

    @Input() id!: string;

    @Input() webserviceConfig: WebserviceConfig;
    @Input() records: ApiFragment[];
    @Input() multiple: boolean;

    @Input() searchLabel: string;
    @Input() placeholderMsg?: string;
    @Input() indeterminateMsg = '';
    @Input() maxValuesMsg?: string;
    @Input() disabledMsg?: string;

    @Input() set isDisabled(disabled: boolean) {
        this.setDisabledState(disabled);
    }

    constructor(
        cd: ChangeDetectorRef,
        private readonly translateService: SharedLanguageService,
        private dialogService: ShDynamicDialogService
    ) {
        super(cd);
    }

    ngOnInit(): void {
        this.initIndeterminateStream();

        if (this.multiple) {
            this.initListValuePropagation();
            this.initTagStream();
            this.initSelectValuesActionItem();
        } else {
            this.initSingleValuePropagation();
            this.initSelectValuesActionItem();
        }

        if (this.disabled) {
            this.webserviceSearchControl.disable();
        }
    }

    protected writeValueImpl(input: FormValue<ComplexMapFieldValue>[]): void {
        if (!input.every(formValue => formValue.value)) {
            console.error(
                'ShFormWebserviceComplexBaseComponent:: value must be Array<FormValue<ComplexMapFieldValue>>',
                input
            );
            return this.writeValueImpl([]);
        }

        this.state$.next(input);

        const inputValues: number = input.filter(formValue => formValue.state === FormValueState.SET).length;

        if (!this.multiple && inputValues <= 1) {
            this.syncSearchControl(input);
        }
    }

    private initIndeterminateStream(): void {
        this.isIndeterminate$ = this.state$.pipe(
            map(formValues => !!formValues.find(formValue => formValue.state === FormValueState.INDETERMINATE))
        );
    }

    private initTagStream(): void {
        this.tags$ = this.state$.pipe(
            map(formValues =>
                formValues.filter(formValue => formValue.state === FormValueState.SET).map(formValue => formValue.value)
            ),
            map(filteredValues => this.complexToFormSelectOptions(filteredValues))
        );
    }

    private complexToFormSelectOptions(concepts: ComplexMapFieldValue[]): MultiFormSelectOptions<ComplexMapFieldValue> {
        return {
            options: concepts.map(concept => ({
                label: this.webserviceConfig.getWebserviceDataLabel(
                    concept,
                    this.translateService.translate('complex.label-not-found')
                ),
                value: concept
            })),
            isLiveData: true
        };
    }

    // #region TreeView Dialog Action Item
    private initSelectValuesActionItem(): void {
        const totalValues$: Observable<number> = this.multiple
            ? this.getTotalListValues()
            : this.getTotalSingleValues();

        this.searchActionItem$ = totalValues$.pipe(map(totalValues => this.getSelectValuesActionItem(totalValues)));
    }

    private getSelectValuesActionItem(totalValues: number): ActionItem | null {
        return {
            style: ActionItemStyle.Link,
            label: this.translateService.translate('actions.search'),
            eventListener: this.openSearchDialog.bind(this),
            disabled: (!this.multiple && totalValues > 0) || this.disabled,
            disabledInfoMsg: this.disabledMsg ?? this.maxValuesMsg
        };
    }

    private openSearchDialog(): void {
        const selection$: Observable<ComplexMapFieldValue[]> = this.initSearchDialog();

        combineLatest([selection$, this.state$])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([selection, currentState]) => {
                this.updateState(selection, currentState);
            });
    }

    private initSearchDialog(): Observable<ComplexMapFieldValue[]> {
        const config: DialogConfig = {
            title: this.translateService.translate('actions.search'),
            options: {
                style: DialogStyle.ExtraWide
            },
            data: {
                yes: this.translateService.translate('actions.add'),
                no: this.translateService.translate('actions.common.cancel'),
                webserviceConfig: this.webserviceConfig,
                multiple: this.multiple
            }
        };

        const ref: DialogRef<SelectionDialogOutput> = this.dialogService.open(WebserviceSearchDialogComponent, config);
        const selection$: Observable<ComplexMapFieldValue[]> = ref.afterClosed$.pipe(
            filter(
                (output: SelectionDialogOutput): output is SelectionDialogOutputSuccess =>
                    output.type === 'success' && output.selection.length > 0
            ),
            map(output => output.selection)
        );

        return selection$;
    }

    private updateState(selection: ComplexMapFieldValue[], currentState: FormValue<ComplexMapFieldValue>[]): void {
        if (!selection || selection.length === 0) {
            return;
        }

        if (!this.multiple && selection.length === 1) {
            this.updateSingleValueField(selection[0]);
        }

        this.updateListValueField(selection, currentState);
    }

    // #endregion

    // #region handle Single value
    private initSingleValuePropagation(): void {
        this.webserviceSearchControl.valueChanges
            .pipe(
                map((values: FormValue<ComplexMapFieldValue>[] | null) =>
                    this.controlValuesToPropagationValues(values)
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(result => {
                this.propagateChange(result);
            });
    }

    private controlValuesToPropagationValues(
        values: FormValue<ComplexMapFieldValue>[] | null
    ): FormValue<ComplexMapFieldValue>[] {
        const formValues: FormValue<ComplexMapFieldValue>[] = values ?? [];

        const setValues: FormValue<ComplexMapFieldValue>[] = formValues.filter(
            formValue => formValue.state === FormValueState.SET
        );

        // propagate new values on set
        if (setValues.length > 0) {
            this.state$.next(setValues);
            return setValues;
        }

        const indeterminateCurrentValues: FormValue<ComplexMapFieldValue>[] = this.currentFormValues.filter(
            formValue => formValue.state === FormValueState.INDETERMINATE
        );

        // unset all current value if not indeterminate
        if (setValues.length === 0 && indeterminateCurrentValues.length === 0) {
            const unsetValues: FormValue<ComplexMapFieldValue>[] = this.currentFormValues.map(
                formValue => new FormValue(formValue.value, FormValueState.UNSET)
            );
            return unsetValues;
        } else {
            // return original values if indeterminate
            return this.currentFormValues;
        }
    }

    private syncSearchControl(input: FormValue<ComplexMapFieldValue>[]): void {
        this.webserviceSearchControl.setValue(input);
    }

    private updateSingleValueField(complexMapFieldValue: ComplexMapFieldValue | null): void {
        if (!complexMapFieldValue) {
            this.webserviceSearchControl.setValue([]);
        } else {
            this.webserviceSearchControl.setValue([new FormValue(complexMapFieldValue)]);
        }
    }

    private getTotalSingleValues(): Observable<number> {
        return this.webserviceSearchControl.valueChanges.pipe(
            map(values => values ?? []),
            map(
                (formValues: FormValue<ComplexMapFieldValue>[]) =>
                    formValues.filter(formValue => formValue.state === FormValueState.SET).length
            )
        );
    }

    // #endregion

    // #region handle List value
    private initListValuePropagation(): void {
        this.state$.pipe(takeUntil(this.destroy$)).subscribe(result => {
            this.propagateChange(result);
        });
    }

    private updateListValueField(list: ComplexMapFieldValue[], currentState: FormValue<ComplexMapFieldValue>[]): void {
        const newValues: FormValue<ComplexMapFieldValue>[] = list
            .filter(item => !currentState.find(formValue => this.isEqual(formValue.value, item)))
            .map(item => new FormValue(item));

        const updatedValues: FormValue<ComplexMapFieldValue>[] = currentState.map(formValue =>
            list.find(complexMapFieldValue => this.isEqual(formValue.value, complexMapFieldValue))
                ? new FormValue(formValue.value)
                : formValue
        );

        const allValues: FormValue<ComplexMapFieldValue>[] = [...updatedValues, ...newValues];

        this.writeValue(allValues);
    }

    private isEqual(object1: ComplexMapFieldValue, object2: ComplexMapFieldValue): boolean {
        return (
            this.webserviceConfig.getWebserviceDataId(object1) === this.webserviceConfig.getWebserviceDataId(object2)
        );
    }

    public removeValue(valueToRemove: ComplexMapFieldValue): void {
        const currentValues: FormValue<ComplexMapFieldValue>[] = this.currentFormValues;

        const foundFormValue: FormValue<ComplexMapFieldValue> | undefined = currentValues.find(formValue =>
            this.isEqual(formValue.value, valueToRemove)
        );

        if (!foundFormValue) {
            return;
        }

        const valueIndex: number = currentValues.indexOf(foundFormValue);

        const newFormValues: FormValue<ComplexMapFieldValue>[] = [...currentValues];

        if (typeof foundFormValue !== 'undefined') {
            newFormValues.splice(valueIndex, 1, new FormValue(foundFormValue.value, FormValueState.UNSET));
        } else {
            newFormValues.splice(valueIndex, 1);
        }

        this.state$.next(newFormValues);
    }

    private getTotalListValues(): Observable<number> {
        return this.state$.pipe(
            map(state => state.filter(formValue => formValue.state === FormValueState.SET)),
            map(values => values.length)
        );
    }

    // #endregion

    public onLookupSelection(complexMapFieldValue: ComplexMapFieldValue | null): void {
        if (!this.multiple || !complexMapFieldValue) {
            return;
        }

        this.updateListValueField([complexMapFieldValue], this.currentFormValues);
    }

    private get currentFormValues(): FormValue<ComplexMapFieldValue>[] {
        return this.state$.value;
    }

    protected override propagateDisabledState(state: boolean): void {
        if (state && this.webserviceSearchControl.enabled) {
            this.webserviceSearchControl.disable();
        } else if (!state && this.webserviceSearchControl.disabled) {
            this.webserviceSearchControl.enable();
        }
    }
}
